"use client";

import Script from "next/script";
import { useCallback, useEffect, useRef } from "react";
import type { TurnstileAction } from "@/lib/turnstile";
import { getTurnstileSiteKey } from "@/lib/turnstile-widget";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SITE_KEY = getTurnstileSiteKey();

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action: TurnstileAction;
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
      theme: "light";
    }
  ) => string;
  reset: (widgetId?: string) => void;
  remove?: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

type Props = {
  action: TurnstileAction;
  onTokenChange: (token: string | null) => void;
  resetSignal?: number;
  className?: string;
};

export default function TurnstileWidget({
  action,
  onTokenChange,
  resetSignal,
  className = "",
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const lastResetSignalRef = useRef(resetSignal);

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  const renderWidget = useCallback(() => {
    if (!SITE_KEY || !containerRef.current || widgetIdRef.current) {
      return;
    }
    const widgetId = window.turnstile?.render(containerRef.current, {
      sitekey: SITE_KEY,
      action,
      callback: (token) => onTokenChangeRef.current(token),
      "expired-callback": () => onTokenChangeRef.current(null),
      "error-callback": () => onTokenChangeRef.current(null),
      theme: "light",
    });
    widgetIdRef.current = widgetId ?? null;
  }, [action]);

  useEffect(() => {
    renderWidget();
    const cleanupWidgetRef = widgetIdRef;
    return () => {
      const widgetId = cleanupWidgetRef.current;
      if (widgetId && window.turnstile?.remove) {
        window.turnstile.remove(widgetId);
      }
      cleanupWidgetRef.current = null;
      onTokenChangeRef.current(null);
    };
  }, [renderWidget]);

  useEffect(() => {
    if (lastResetSignalRef.current === resetSignal) return;
    lastResetSignalRef.current = resetSignal;
    if (!widgetIdRef.current) return;
    window.turnstile?.reset(widgetIdRef.current);
    onTokenChangeRef.current(null);
  }, [resetSignal]);

  if (!SITE_KEY) return null;

  return (
    <>
      <Script
        id="cloudflare-turnstile"
        src={SCRIPT_SRC}
        strategy="afterInteractive"
        onLoad={renderWidget}
        onReady={renderWidget}
      />
      <div
        ref={containerRef}
        className={className || "flex min-h-[65px] justify-center"}
      />
    </>
  );
}
